import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Badge,
  Skeleton,
  Table,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import moment from "moment";
import { useResponsive } from "../../context/ResponsiveContext";
import {
  ClientPackageAdjustment,
  ClientPackageAdjustmentType,
  PackageReasonCategory,
  PACKAGE_REASON_CATEGORIES,
} from "../../types/packages";
import { parseServerDateTime } from "../../utils/localDateUtils";

const ADJUSTMENT_TYPE_LABELS: Record<ClientPackageAdjustmentType, string> = {
  grant_package: "Granted",
  adjust_bank: "Adjusted",
  edit_expiry: "Expiry changed",
  revoke_package: "Revoked",
};

const REASON_LABELS: Record<PackageReasonCategory, string> = Object.fromEntries(
  PACKAGE_REASON_CATEGORIES.map((r) => [r.value, r.label]),
) as Record<PackageReasonCategory, string>;

const adjustmentTypeColor = (type: ClientPackageAdjustmentType): string => {
  if (type === "revoke_package") return "red";
  if (type === "grant_package") return "var(--brand-teal)";
  return "gray";
};

/** Prefix a positive unit delta with '+' (negatives already carry '-'). */
const signedUnits = (n: number): string => `${n > 0 ? "+" : ""}${n}`;

const formatExpiry = (value: string | null): string =>
  value ? moment(value).format("MMM D, YYYY") : "No expiry";

/** Format an audit timestamp in the viewer's local zone (server sends UTC). */
function formatAuditDateTime(value: string): string {
  const parsed = parseServerDateTime(value);
  return parsed ? moment(parsed).format("MMM D, YYYY h:mm A") : "—";
}

/**
 * The "Change" cell is rendered per adjustment type: grant/adjust show a signed
 * unit delta, edit-expiry shows before → after dates, revoke shows the
 * forfeited units.
 */
function renderChange(adj: ClientPackageAdjustment): React.ReactNode {
  switch (adj.adjustment_type) {
    case "grant_package":
      return (
        <Text size="sm" fw={500} c="var(--brand-teal)">
          {signedUnits(adj.delta_remaining)} units
        </Text>
      );
    case "adjust_bank": {
      // Prefer the non-zero delta so a total-only edit still reads meaningfully.
      const delta =
        adj.delta_remaining !== 0 ? adj.delta_remaining : adj.delta_total;
      return (
        <Text size="sm" fw={500} c={delta < 0 ? "red" : "var(--brand-teal)"}>
          {adj.service_name ? `${adj.service_name}: ` : ""}
          {signedUnits(delta)} units
        </Text>
      );
    }
    case "edit_expiry":
      return (
        <Text size="sm">
          {formatExpiry(adj.expiry_before)} → {formatExpiry(adj.expiry_after)}
        </Text>
      );
    case "revoke_package":
      return (
        <Text size="sm" fw={500} c="red">
          Forfeited {Math.abs(adj.delta_remaining)} units
        </Text>
      );
    default:
      return (
        <Text size="sm" c="dimmed">
          —
        </Text>
      );
  }
}

/** Read-only audit table of manual adjustments, newest-first. */
function PackageHistoryTable({
  adjustments,
}: {
  adjustments: ClientPackageAdjustment[];
}) {
  const sortedAdjustments = [...adjustments].sort(
    (a, b) =>
      (parseServerDateTime(b.created_dt)?.getTime() ?? 0) -
      (parseServerDateTime(a.created_dt)?.getTime() ?? 0),
  );
  if (adjustments.length === 0) {
    return (
      <Text size="sm" c="dimmed" py="xs">
        No adjustments recorded for this package.
      </Text>
    );
  }
  return (
    <Table.ScrollContainer minWidth={620}>
      <Table verticalSpacing="xs" fz="sm" striped withRowBorders={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Date</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Change</Table.Th>
            <Table.Th>Reason</Table.Th>
            <Table.Th>Note</Table.Th>
            <Table.Th>Staff</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sortedAdjustments.map((adj) => (
            <Table.Tr key={adj.id}>
              <Table.Td>{formatAuditDateTime(adj.created_dt)}</Table.Td>
              <Table.Td>
                <Badge
                  size="sm"
                  variant="light"
                  color={adjustmentTypeColor(adj.adjustment_type)}
                >
                  {ADJUSTMENT_TYPE_LABELS[adj.adjustment_type]}
                </Badge>
              </Table.Td>
              <Table.Td>{renderChange(adj)}</Table.Td>
              <Table.Td>
                {adj.reason_category ? REASON_LABELS[adj.reason_category] : "—"}
              </Table.Td>
              <Table.Td>{adj.note || "—"}</Table.Td>
              <Table.Td>{adj.created_by_name ?? adj.created_by}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

interface PackageHistoryModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  /** The owned package (client_service_package) id whose history to show. */
  cspId: string;
  packageName: string;
  getAdjustments: (
    salonId: string,
    cspId: string,
  ) => Promise<ClientPackageAdjustment[]>;
  /** Bumped after any successful action so an open modal reloads its history. */
  historyToken: number;
}

/**
 * Read-only modal showing a single package's manual-adjustment audit history.
 * Fetches lazily when opened and refetches when `historyToken` changes while
 * open (so a mutation elsewhere is reflected). Replaces the previous inline,
 * expand-in-card history table.
 */
export default function PackageHistoryModal({
  opened,
  onClose,
  salonId,
  cspId,
  packageName,
  getAdjustments,
  historyToken,
}: PackageHistoryModalProps) {
  const { isMobile } = useResponsive();
  const [adjustments, setAdjustments] = useState<
    ClientPackageAdjustment[] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const openedRef = useRef(false);

  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = await getAdjustments(salonId, cspId);
      setAdjustments(list);
    } catch (err) {
      console.error("Error fetching package adjustments:", err);
      setError("Failed to load history.");
    } finally {
      setIsLoading(false);
    }
  }, [getAdjustments, salonId, cspId]);

  // Fetch on open; refetch on a later historyToken bump while still open.
  useEffect(() => {
    if (opened && !openedRef.current) {
      openedRef.current = true;
      loadHistory();
    } else if (!opened) {
      openedRef.current = false;
    }
  }, [opened, loadHistory]);

  useEffect(() => {
    if (opened && openedRef.current && historyToken > 0) {
      loadHistory();
    }
  }, [historyToken, opened, loadHistory]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{`Package history - ${packageName}`}</Text>}
      size="xl"
      fullScreen={isMobile}
      centered
    >
      <Stack gap="md">
        {isLoading ? (
          <Stack gap="xs">
            <Skeleton height={28} radius="sm" />
            <Skeleton height={28} radius="sm" />
            <Skeleton height={28} radius="sm" />
          </Stack>
        ) : error ? (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            <Group justify="space-between" align="center">
              <Text size="sm">{error}</Text>
              <Button
                size="compact-xs"
                variant="light"
                color="red"
                onClick={loadHistory}
              >
                Retry
              </Button>
            </Group>
          </Alert>
        ) : adjustments ? (
          <PackageHistoryTable adjustments={adjustments} />
        ) : null}
      </Stack>
    </Modal>
  );
}
